import { fromSpecific, TypedNode } from '@rehaje/core'
import { reactive } from '@rehaje/reactive'
import { isEmpty, pDebounceThrottle, toArr } from '@rehaje/shared'

import { MustacheNode } from '../Mustache'
import { ActionTriggerEvent } from './event'

import type { AnyExpressionSpecific } from '../Mustache'
import type {
  AnyTypedNode,
  TypedSpecific,
  TypedSpecificLike,
  ViewScope
} from '@rehaje/core'
import type {
  PromiseDebounceThrottleOptions,
  RecursionType,
  TRecord
} from '@rehaje/shared'

export type ActionProps = RecursionType<TypedSpecific>

export interface ActionResult {
  action?: (() => Promise<void>)[] | (() => Promise<void>)
  condition?: boolean
  debounceTime?: number
  props?: TRecord
  throttleTime?: number
}

export interface ActionSpecific extends TypedSpecificLike {
  action?: AnyActionNode | AnyActionNode[]
  condition?: AnyExpressionSpecific | boolean
  debounceTime?: AnyExpressionSpecific | number
  props?: ActionProps
  throttleTime?: AnyExpressionSpecific | number
  type: `Action.${string}`
}

export type AnyActionNode = AnyTypedNode & { type: `Action.${string}` }

export type AnyActionSpecific = TypedSpecific & { type: `Action.${string}` }

export abstract class ActionNode extends TypedNode {
  __r_action_maps: TRecord<(...args: any[]) => any> = {}

  @fromSpecific()
  @reactive('shallow')
  action?: AnyActionNode | AnyActionNode[] | MustacheNode | MustacheNode[]

  @fromSpecific()
  @reactive()
  condition?: boolean | MustacheNode

  @fromSpecific()
  @reactive()
  debounceTime?: MustacheNode | number

  @fromSpecific()
  @reactive('deep')
  props: ActionProps = {}

  @fromSpecific()
  @reactive()
  throttleTime?: MustacheNode | number

  getDebounceThrottleOptions(options: PromiseDebounceThrottleOptions) {
    return options
  }

  /**
   * 动作实现，必须重载实现
   * @param props
   */
  abstract handler(props: any, scope: ViewScope): Promise<void>

  /**
   * 是否应该触发动作，如果 condition 节点是表达式节点，但是表达式为空字符串，则认为应该触发
   * 其他情况根据表达式编译结果判断，true 或者 undefined 表示应该触发，false 表示不应该触发
   */
  async isShouldTrigger(result: ActionResult): Promise<boolean> {
    const node = this.condition
    if (node instanceof MustacheNode && isEmpty(node.code)) return true
    return result.condition !== false
  }

  override transpile() {
    const debounceTime = this.evaluate(['debounceTime'])
    const throttleTime = this.evaluate(['throttleTime'])
    const actionKey = `${this.__r_indexes}#${debounceTime}#${throttleTime}`

    this.__r_action_maps[actionKey] ??= pDebounceThrottle(async () => {
      const scope = this.getSelfViewScope()!
      const result = super.transpile<ActionResult>(scope)
      const shouldTrigger = await this.isShouldTrigger(result)
      if (!shouldTrigger) return
      this.dispatchEvent(new ActionTriggerEvent())
      await this.handler?.(result.props, scope)
      await Promise.all(
        toArr(result.action)
          .filter((action) => typeof action === 'function')
          .map((action) => action())
      )
    }, this.getDebounceThrottleOptions({ debounceTime, throttleTime }))

    return this.__r_action_maps[actionKey]
  }
}

declare global {
  export interface RehajeExtendsSpecifics {
    ExtendsAction: ActionSpecific
  }
}
